// ============================================================================
// crmAvisoContrato · o combinado entre as 3 partes do aviso de mensagem nova (30/10/2026)
// ============================================================================
// Pedido do dono: "notificação de mensagem para quando a recepcionista estiver no
// computador e estiver fora do DNA, por exemplo no Avec, ou em outra aba do DNA e não
// tiver no CRM ... elas estão se perdendo. O som também não está funcionando."
//
// As 3 partes que precisam falar a mesma língua:
//   1. o servidor (api/_lib/crm-sofia-aviso-mensagem.js, chamado pela entrada do WhatsApp):
//      push do sistema para o computador da recepção;
//   2. o vigia da tela (src/components/crm/VigiaCrm.jsx, montado em todas as telas do DNA
//      para quem tem o CRM): som, título "(N)" e aviso do navegador quando não há push;
//   3. o service worker (public/push-handler.js) e a inscrição do computador
//      (src/utils/crmAvisoComputador.js).
// SEM import nenhum: o servidor e a tela importam este arquivo.
// Nada de dado do cliente aqui: nem nome, nem telefone, nem texto (tela bloqueada, LGPD).
// ============================================================================

/** O tipo que vai no payload do push e no data da notificação. */
export const TIPO_AVISO_MENSAGEM = 'crm-msg';
/** O começo da tag de todo aviso de mensagem (o service worker reconhece por ele; cópia em public/push-handler.js). */
export const PREFIXO_TAG_AVISO = 'crm-msg-';
/** A tag do aviso de teste da Aparência (o service worker trata como aviso de mensagem). */
export const TAG_AVISO_TESTE = 'crm-msg-teste';

/** A tag da notificação de uma conversa: o push e o aviso da página usam a MESMA (um substitui o outro).
 *  Curta e só com [a-z0-9-] porque o topic do web push corta em 32 e aceita só base64url. */
export function tagAvisoMensagem(conversaId) {
  const hex = String(conversaId || '').toLowerCase().replace(/[^0-9a-f]/g, '').slice(0, 12);
  return hex ? `${PREFIXO_TAG_AVISO}${hex}` : TIPO_AVISO_MENSAGEM;
}

/** Para onde o clique leva (o lerEstadoTrabalho do CRM entende aba e conversa). */
export function urlAvisoMensagem(conversaId) {
  return conversaId ? `/gestao/crm-sofia?aba=caixa&conversa=${encodeURIComponent(conversaId)}` : '/gestao/crm-sofia?aba=caixa';
}

export const TITULO_AVISO_MENSAGEM = 'Mensagem nova no WhatsApp';

/** O corpo: só a unidade (curta, ex.: "Moema"). Sem unidade: "Conversa sem unidade". */
export function corpoAvisoMensagem(unidadeCurta) {
  const u = String(unidadeCurta || '').trim();
  return `${u || 'Conversa sem unidade'} · clique para abrir a conversa`;
}

/** A hora (ISO) válida ou null. */
function isoOuNulo(v) {
  const n = v ? Date.parse(v) : Number.NaN;
  return Number.isFinite(n) ? new Date(n).toISOString() : null;
}

/**
 * O payload inteiro do push (o servidor monta com isto; o service worker lê payload.tipo).
 * em = a hora (do servidor) da fala do cliente que gerou o aviso: a tela marca essa fala como avisada
 * (crmAvisados.js) e nem a Caixa nem o vigia tocam de novo por ela. Não é dado do cliente.
 */
export function payloadAvisoMensagem({ conversaId, unidadeCurta = null, em = null } = {}) {
  return {
    title: TITULO_AVISO_MENSAGEM,
    body: corpoAvisoMensagem(unidadeCurta),
    tag: tagAvisoMensagem(conversaId),
    url: urlAvisoMensagem(conversaId),
    requireInteraction: true,
    tipo: TIPO_AVISO_MENSAGEM,
    data: { tipo: TIPO_AVISO_MENSAGEM, conversaId: conversaId || null, em: isoOuNulo(em) },
  };
}
/** O push de mensagem velha não serve: o TTL (s) que o servidor manda ao serviço de push. */
export const TTL_AVISO_MENSAGEM_S = 600;
/** O teto da execução inteira do aviso no servidor (leituras, trava e envio). */
export const PRAZO_AVISO_MS = 8000;

/** Inscrição de COMPUTADOR (o aviso de mensagem vai só para elas, não para o iPhone pessoal).
 *  iPad com Safari se diz Macintosh: entra como computador (é o balcão, não o bolso). */
export function eComputador(userAgent) {
  const ua = String(userAgent || '');
  if (!ua) return false;
  if (/iPhone|iPod|Android|Mobile|Windows Phone|webOS|BlackBerry/i.test(ua)) return false;
  return /Macintosh|Windows NT|X11|Linux|CrOS/i.test(ua);
}

// ── a inscrição do computador é SEPARADA do push pessoal (30/10/2026, revisão) ──
// O botão "Ativar avisos" grava a linha de push_subscriptions com professional_id = 'crm-pc:<id>'
// (o id vem da SESSÃO no /api/push-subscribe, nunca do corpo). Assim o computador do balcão recebe SÓ o
// aviso de mensagem (nada de duelo, aniversário ou cartão da fila de quem ativou), o push pessoal
// (sendPush, atendimento-push) nem enxerga essa linha, e o aviso de mensagem só vai para quem ativou.
/** O corpo do POST /api/push-subscribe e /api/push-unsubscribe leva { destino: DESTINO_PUSH_COMPUTADOR }. */
export const DESTINO_PUSH_COMPUTADOR = 'crm-computador';
export const PREFIXO_PUSH_COMPUTADOR = 'crm-pc:';
/** A chave da linha do computador em push_subscriptions.professional_id. Sem id: null. */
export function idPushComputador(id) {
  const s = id == null ? '' : String(id).trim();
  return s ? `${PREFIXO_PUSH_COMPUTADOR}${s}` : null;
}
/** O id da pessoa de uma linha 'crm-pc:<id>'; outra linha qualquer: null. */
export function idDaLinhaComputador(professionalId) {
  const s = String(professionalId || '');
  return s.startsWith(PREFIXO_PUSH_COMPUTADOR) && s.length > PREFIXO_PUSH_COMPUTADOR.length ? s.slice(PREFIXO_PUSH_COMPUTADOR.length) : null;
}

// ── localStorage da tela (por navegador) ──
/** { [conversaId]: ultimaDoClienteEmISO } do que JÁ foi avisado (som ou aviso), por qualquer aba.
 *  O vigia e a Caixa consultam antes de tocar: a mesma fala nunca toca duas vezes. */
export const CHAVE_AVISADOS = 'fk.crm.avisados';
export const AVISADOS_TTL_MS = 24 * 3_600_000;
export const AVISADOS_MAX = 300;
/** { id, em } de quem inscreveu ESTE navegador no push do computador (crmAvisoComputador.js grava). */
export const CHAVE_PUSH_COMPUTADOR = 'fk.crm.pushComputador';
/** ISO da última leitura boa do vigia: na volta (recarga pelo deploy) o que chegou depois disso ainda avisa. */
export const CHAVE_VIGIA_DESDE = 'fk.crm.vigiaDesde';
/** { total, em } das conversas com fala nova, que a aba líder escreve e as outras abas leem (título). */
export const CHAVE_VIGIA_TOTAL = 'fk.crm.vigiaTotal';
/** { [tag]: { em, rec } }: o push daquela conversa CHEGOU a este navegador (em = a hora da fala no servidor,
 *  rec = Date.now() da chegada). Quem grava é a tela, ao receber MSG_PUSH_CHEGOU do service worker. O vigia
 *  só cai no aviso da página quando o push daquela fala nunca chegou (fechar o push no X não reavisa). */
export const CHAVE_PUSH_CHEGOU = 'fk.crm.pushChegou';
export const PUSH_CHEGOU_TTL_MS = 60 * 60_000;
export const PUSH_CHEGOU_MAX = 100;
/** A líder pede o som à aba do DNA que está em foco ({ n, em }); a aba que tocou responde { n, r } em
 *  CHAVE_VIGIA_TOCOU (r = 'tocou' | 'intervalo' | 'travado' | 'mudo'). Sem resposta em ESPERA_TOCOU_MS: ninguém em foco. */
export const CHAVE_VIGIA_TOCAR = 'fk.crm.vigiaTocar';
export const CHAVE_VIGIA_TOCOU = 'fk.crm.vigiaTocou';
export const ESPERA_TOCOU_MS = 1200;
/** As outras chaves do vigia neste navegador (registradas aqui para ninguém colidir):
 *  'fk.crm.caixaAtiva' (crmCaixaAtiva.js) e 'fk.crm.vigiaLider' (crmVigia.js). */
export const CHAVE_CAIXA_ATIVA = 'fk.crm.caixaAtiva';
export const CHAVE_LIDER = 'fk.crm.vigiaLider';
/** Com o push ativo, o vigia espera isto (mais que PRAZO_AVISO_MS do servidor, com folga para a função fria)
 *  antes de cair no aviso da página quando o push daquela fala não chegou. Subiu o prazo do servidor, sobe aqui. */
export const ESPERA_PUSH_MS = 15_000;
/** O som que não saiu (o navegador só libera o áudio com um gesto): a Caixa dispara este evento na janela e o
 *  vigia mostra "Clique aqui para ligar o som do CRM". detail = { volume }. */
export const EVENTO_SOM_TRAVADO = 'fk-crm-som-travado';

// ── as mensagens entre o service worker e a página (cópias em public/push-handler.js, que não tem import) ──
/** Clique no aviso com o DNA aberto: o service worker manda { tipo: MSG_ABRIR_CONVERSA, url } com um
 *  MessageChannel; o vigia navega sem recarregar e responde { ok: true } pela porta. Sem resposta em
 *  ESPERA_ABRIR_MS (o vigia não está montado): o service worker navega a janela (client.navigate). */
export const MSG_ABRIR_CONVERSA = 'fk-crm-abrir-conversa';
export const ESPERA_ABRIR_MS = 1000;
/** Push de mensagem chegou: o service worker manda { tipo: MSG_PUSH_CHEGOU, tag, conversaId, em, pedirSom }
 *  para TODAS as janelas do DNA (a tela grava CHAVE_PUSH_CHEGOU e marca a fala como avisada). Para a janela
 *  EM FOCO vai pedirSom: true com um MessageChannel: se a página tocou o som do CRM (ou a fala já estava
 *  avisada), responde { tocou: true } e o aviso do sistema sai SEM som; sem resposta em ESPERA_SOM_MS, ou
 *  { tocou: false } (som travado, desligado, sem vigia), o aviso sai com o som do sistema. Um som só, sempre. */
export const MSG_PUSH_CHEGOU = 'fk-crm-push-chegou';
export const ESPERA_SOM_MS = 800;
